// Modules
import { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';

// CSS and assets
import './style.scss';

// Components
import { useAuth } from '@global/hooks/useAuth';

import { NotActive, NotExist } from '@pages/SubscriptionDetails/components';

// Utils
import analytic from '@global/utils/AdobeAnalytics';
import { waitForAdobeAnalytics } from '@global/utils/waitForAdobeAnalytics';
import { PageLayerType } from '@global/utils/AdobeAnalytics/types';

import { useAppDispatch, useAppSelector } from '@global/store/hooks';
import { getSubscriptions } from '@global/store/slices/subscription/extraReducers';
import { subscriptionSelector } from '@global/store/slices/subscription/selectors';
import { getEntitlements } from '@global/store/slices/entitlements/extraReducers/getEntitlements';

import { existSubscriptionStatus } from '@global/store/slices/subscription/helper';
import { SiteConfig } from '@global/utils/SiteConfig';
import { Entitlement } from '@global/requests/entitlements';
import { hasActiveEntitlementForSku } from '@global/hooks/useEntitlementPolling/utils';

import { SubscriptionContainer } from './components/SubscriptionContainer';
import { ProfileInformation } from './components/ProfileInformation';

const pageLayerProps: PageLayerType = {
  page: {
    name: 'subscriptions-details',
    section: 'subscription details',
    subSection: '',
  },
  event: 'pageView',
};

export const Subscription: React.FC = () => {
  const { isLoggedIn, isLoading, user } = useAuth();
  const { isLoading: isLoadingSubscription, subscriptionsByUid } =
    useAppSelector(subscriptionSelector);

  const [allEntitlements, setAllEntitlements] = useState<Entitlement[]>([]);
  const [isLoadingEntitlements, setIsLoadingEntitlements] = useState(false);

  const dispatch = useAppDispatch();

  const history = useHistory();

  const { isB2CSite } = SiteConfig.getConfig();

  useEffect(() => {
    if (!isLoggedIn && !isLoading) {
      history.goBack();
    }

    if (isLoggedIn) {
      waitForAdobeAnalytics(() => {
        analytic.dataLayerPageLoaded({ ...pageLayerProps, isLoggedIn });
      });
    }
  }, [user]);

  useEffect(() => {
    window.scrollTo(0, 0);
    dispatch(getSubscriptions({ userUid: user?.uid }));
  }, [user]);

  useEffect(() => {
    const fetchEntitlements = async () => {
      if (isLoadingSubscription) return;
      if (!isB2CSite) return;
      if (!subscriptionsByUid || Object.keys(subscriptionsByUid).length === 0) {
        return;
      }

      setIsLoadingEntitlements(true);

      try {
        // Fetch all entitlements for the user in a single call
        const result = await dispatch(getEntitlements()).unwrap();
        setAllEntitlements(result);
      } catch (error) {
        console.error('Failed to fetch entitlements:', error);
        setAllEntitlements([]);
      }

      setIsLoadingEntitlements(false);
    };

    fetchEntitlements();
  }, [isLoadingSubscription, subscriptionsByUid, isB2CSite, dispatch]);

  const filteredSubscriptions = useMemo(() => {
    if (!isB2CSite) {
      return subscriptionsByUid;
    }

    if (allEntitlements.length === 0) {
      return {};
    }

    const filtered: typeof subscriptionsByUid = {};

    // For B2C sites (SpectraBase), check exact SKU match
    Object.entries(subscriptionsByUid).forEach(([key, subscription]) => {
      const hasActiveEntitlement = hasActiveEntitlementForSku(
        allEntitlements,
        subscription.product.maId
      );

      if (hasActiveEntitlement) {
        filtered[key] = subscription;
      }
    });

    return filtered;
  }, [subscriptionsByUid, allEntitlements, isB2CSite]);

  const isLoadingData =
    isLoading ||
    (isLoadingSubscription &&
      !subscriptionsByUid[Object.keys(subscriptionsByUid)[0]]?.uid) ||
    (isB2CSite && isLoadingEntitlements);

  if (isLoadingData) {
    return <div className="page-spinner"></div>;
  }

  // If subscription purchased but not active yet
  if (
    existSubscriptionStatus.includes(
      filteredSubscriptions[Object.keys(filteredSubscriptions)[0]]?.orderStatus
    )
  ) {
    return <NotActive />;
  }

  // If subscription does not exist
  if (Object?.keys(filteredSubscriptions)?.length === 0) {
    return <NotExist />;
  }

  return (
    <>
      <div className="subscription">
        <div className="subscription__container">
          <section className="subscription__data-active">
            <ProfileInformation
              subscription={
                filteredSubscriptions[Object.keys(filteredSubscriptions)[0]]
              }
            />
            {Object.keys(filteredSubscriptions).map((key) => {
              const subscription = filteredSubscriptions[key];
              return (
                <SubscriptionContainer
                  key={subscription.uid}
                  subscriptionUid={subscription?.uid}
                />
              );
            })}
          </section>
        </div>
      </div>
    </>
  );
};
